import { formatDate } from '@veracity/vui'
import { useEffect, useMemo, useState } from 'react'

import { PageFilterPanel } from '@/components'
import { useSystemDictionaries } from '@/hooks'
import { CalculationStates, PageFilterPanelPreviewItem, SystemConfigurations } from '@/interfaces'
import { calculationStatusDictionary } from '@/utils'

import { ProjectScenarioFilterValues } from '../../types'
import { ProjectDetailsScenariosFilterPanelInputs } from './ProjectDetailsScenariosFilterPanelInputs'

interface Props {
  isFilterActive: boolean
  onFilterCloseClicked: () => void
  onFilterChange: (filterValues?: ProjectScenarioFilterValues) => void
}

export const ProjectDetailsScenariosFilterPanel = ({
  isFilterActive,
  onFilterChange,
  onFilterCloseClicked
}: Props) => {
  const { systemConfigurations } = useSystemDictionaries()

  const [selectedCalculationState, setSelectedCalculationState] =
    useState<CalculationStates>()
  const [selectedSystemConfiguration, setSelectedSystemConfiguration] =
    useState<SystemConfigurations>()
  const [selectedCalculationStartDate, setSelectedCalculationStartDate] =
    useState<Date>()

  const previewFilterItems = useMemo(() => {
    const items: PageFilterPanelPreviewItem[] = []

    if (selectedCalculationState !== undefined)
      items.push({
        label: calculationStatusDictionary[selectedCalculationState] || 'Unknown',
        onFilterClear: () => setSelectedCalculationState(undefined)
      })
    if (selectedSystemConfiguration !== undefined)
      items.push({
        label:
          systemConfigurations.find(
            x => +x.value === selectedSystemConfiguration
          )?.text || '',
        onFilterClear: () => setSelectedSystemConfiguration(undefined)
      })
    if (selectedCalculationStartDate !== undefined)
      items.push({
        label: formatDate(selectedCalculationStartDate, 'DD MMM YYYY'),
        onFilterClear: () => setSelectedCalculationStartDate(undefined)
      })
    return items
  }, [
    selectedCalculationStartDate,
    selectedCalculationState,
    selectedSystemConfiguration,
    systemConfigurations
  ])

  const handleClearAllFilters = () => {
    setSelectedCalculationState(undefined)
    setSelectedSystemConfiguration(undefined)
    setSelectedCalculationStartDate(undefined)
  }

  useEffect(() => {
    if (
      selectedCalculationState === undefined &&
      selectedSystemConfiguration === undefined &&
      selectedCalculationStartDate === undefined
    )
      onFilterChange(undefined)
    else
      onFilterChange({
        selectedCalculationState,
        selectedSystemConfiguration,
        selectedCalculationStartDate
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCalculationStartDate,
    selectedCalculationState,
    selectedSystemConfiguration
  ])

  return (
    <PageFilterPanel
      isFilterPanelActive={isFilterActive}
      onCloseClick={onFilterCloseClicked}
      filterPreviewItems={previewFilterItems}
      onClearAllFiltersClicked={handleClearAllFilters}
      filtersSlot={
        <ProjectDetailsScenariosFilterPanelInputs
          availableSystemConfigurations={systemConfigurations}
          onCalculationStateChanged={setSelectedCalculationState}
          onSystemConfigurationChanged={setSelectedSystemConfiguration}
          onCalculationStartDateRangeChanged={setSelectedCalculationStartDate}
        />
      }
    />
  )
}
